/**
 * The list-verdict step body: one card — "The list is terrible when …",
 * "only when" its guards hold (enough settled SMS, the settle time, and —
 * 20/10/2026 — on how many of today's drops it happened, each drop judged
 * on its own numbers) — and what a FAIL does, as rows in the order things
 * happen: the burn and what today does (Stop list / Complete list — the
 * "A list fails" choice, 23/10/2026; a FAILed list stays out until Reset pool).
 * Everything else is a ⓘ or the "How a day is judged" story on demand
 * (user, 13/10/2026: clean, minimal, understandable at a glance).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Rows3, XCircle } from 'lucide-react';

import { HintDot } from '@/components/ui';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import type { ContainerEditor } from '../state/useContainerEditor';
import { OnlyWhen } from './card/only-when/OnlyWhen';
import { VerdictCard } from './card/VerdictCard';
import { FailConsequence } from './fail-consequence/FailConsequence';
import { HowJudged } from './explainer/HowJudged';
import { VerdictStory } from './explainer/VerdictStory';
import { HistoryLine } from './history/HistoryLine';
import { withSuggestion } from './history/history-words';
import { ReplayPanel } from './replay/ReplayPanel';
import { RightNowLine } from './source/RightNowLine';
import { rowsForSource } from './source/source-words';

const JUDGED_HINT =
  "Each group sends its whole day to one list. The judge adds up the drops this container sent today to that list — SMSWiser's sent, delivered, SMS clicks and bot clicks, the tracker's visits, conversions and revenue — and recomputes the rates from the totals (never averaged). A drop counts once its settle time has passed.";
export const ListVerdictStep: FC<{
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  containerId: string | null;
}> = ({ editor, plan, containerId }) => {
  const { verdict, scaling, scalingEnabled, failMode } = editor.draft;
  const patch = editor.verdictPatch;
  const shrinks = scalingEnabled && (scaling.weak.advanced || scaling.weak.rows.length > 0);
  // Phase 39 — the source: today's drops (DROP tags) or the list's own stats over a range (LIST tags).
  const onDrops = verdict.source.kind === 'drops';
  const starter = onDrops ? { metric: 'deliveredRate', op: '<', value: 0.85 } : { metric: 'cr', op: '<', value: 2 };
  return (
    <div className="flex flex-col gap-3">
      <VerdictCard
        card="listVerdict"
        source={verdict.source}
        onSource={(source) => patch({ source, rows: rowsForSource(verdict.rows, source.kind) })}
        icon={Rows3}
        lead={
          <>
            The list is <span className="text-destructive">terrible</span>
          </>
        }
        aside={<HintDot label={JUDGED_HINT} />}
        rule={verdict}
        onPatch={patch}
        starter={starter}
        footer={
          <OnlyWhen
            sample={{ value: verdict.minSampleSent, onChange: (minSampleSent) => patch({ minSampleSent }), unit: onDrops ? 'sms' : 'visits' }}
            settle={onDrops ? { value: verdict.evaluationDelayMin, onChange: (evaluationDelayMin) => patch({ evaluationDelayMin }) } : undefined}
            times={onDrops ? { value: verdict.timesN, onChange: (timesN) => patch({ timesN }), mode: verdict.timesMode, onMode: (timesMode) => patch({ timesMode }) } : undefined}
          />
        }
        outcome={{ tone: 'destructive', icon: XCircle, label: 'FAIL' }}
      >
        <FailConsequence editor={editor} />
        {onDrops ? (
          <HistoryLine countryKey={plan.countryKey} id={containerId ?? editor.draft.masterId} kind="container" which="terrible" onUse={(s) => patch({ rows: withSuggestion(verdict.rows, s) })} />
        ) : (
          <RightNowLine countryKey={plan.countryKey} containerId={containerId} card="listVerdict" source={verdict.source} rule={verdict} minSampleSent={verdict.minSampleSent} />
        )}
      </VerdictCard>
      <HowJudged>
        <VerdictStory
          delayMin={verdict.evaluationDelayMin}
          minSample={verdict.minSampleSent}
          timesN={verdict.timesN}
          timesMode={verdict.timesMode}
          failMode={failMode}
          scaling={scalingEnabled ? { stepPct: scaling.stepPct, maxAmount: scaling.maxAmount } : null}
          shrink={shrinks ? { shrinkPct: scaling.shrinkPct } : null}
        />
      </HowJudged>
      <ReplayPanel kind="verdict" draft={editor.draft} plan={plan} containerId={containerId} />
    </div>
  );
};
